import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContracts } from '../../api/contract';
import { useCompanyStore } from '../../store/companyStore';
import { Contract, ContractStage } from '../../types/contract';
import { MENU_CONTRACT } from '../layout/Header';
import './ListWidgets.css';
import './OrderProgressWidget.css';

type StageVisual = 'active' | 'inactive' | 'done';

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function resolveStageStatus(
  stages: ContractStage[],
  stage: ContractStage,
  contract: Contract
): StageVisual {
  const currentIndex = stages.findIndex((item) => {
    if (contract.currentStageId != null && item.stageId != null) {
      return item.stageId === contract.currentStageId;
    }
    return item.stageName === contract.currentStage;
  });
  const index = stages.indexOf(stage);
  if (currentIndex < 0) {
    return index === 0 ? 'active' : 'inactive';
  }
  if (index < currentIndex) return 'done';
  if (index === currentIndex) return 'active';
  return 'inactive';
}

function OrderProgressWidget() {
  const navigate = useNavigate();
  const companyId = useCompanyStore((s) => s.companyId);
  const [rows, setRows] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await fetchContracts();
      const active = list.filter((item) => !item.deletedAt);
      setRows(active.slice(0, 8));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [companyId, load]);

  const openContracts = (contractId?: number) => {
    navigate('/app', {
      state: {
        activeMenu: MENU_CONTRACT,
        ...(contractId != null ? { contractId } : {}),
      },
    });
  };

  const displayRows = useMemo(() => rows, [rows]);

  return (
    <section className="ec-widget ec-order">
      <div className="ec-widget__header">
        <div className="ec-list-widget__title">
          <strong>계약서</strong>
          <span className="ec-order__subtitle">진행 단계 현황</span>
        </div>
        <div className="ec-widget__actions">
          <button type="button" className="ec-tool-btn" title="새로고침" onClick={() => void load()}>
            ↻
          </button>
          <button type="button" className="ec-tool-btn" title="계약서 열기" onClick={() => openContracts()}>
            ⋯
          </button>
        </div>
      </div>

      <div className="ec-table-wrap">
        <table className="ec-table ec-order__table">
          <thead>
            <tr>
              <th style={{ width: '140px' }}>문서번호</th>
              <th style={{ width: '180px' }}>계약명</th>
              <th style={{ width: '120px' }}>발주처</th>
              <th style={{ width: '100px' }}>기준일자</th>
              <th>진행단계</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5}>불러오는 중...</td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <button type="button" className="ec-table__link" onClick={() => openContracts()}>
                    등록된 계약서가 없습니다. 계약서에서 작성하기
                  </button>
                </td>
              </tr>
            ) : (
              displayRows.map((contract) => {
                const stages = contract.stages ?? [];
                return (
                  <tr key={contract.contractId}>
                    <td>
                      <div className="ec-order__doc">
                        <button
                          type="button"
                          className="ec-table__link"
                          onClick={() => openContracts(contract.contractId)}
                          title={
                            [
                              contract.documentNo || `#${contract.contractId}`,
                              contract.contractNo ? `계약번호 ${contract.contractNo}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          }
                        >
                          {contract.documentNo || `#${contract.contractId}`}
                          {contract.contractNo ? ` (${contract.contractNo})` : ''}
                        </button>
                        {contract.draft ? <span className="ec-order__draft">임시</span> : null}
                        {contract.urgent ? <span className="ec-order__urgent">긴급</span> : null}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ec-table__link"
                        title={contract.contractName}
                        onClick={() => openContracts(contract.contractId)}
                      >
                        {contract.contractName}
                      </button>
                    </td>
                    <td title={contract.clientName || undefined}>{contract.clientName || '-'}</td>
                    <td>{formatDate(contract.startDate || contract.createdAt)}</td>
                    <td className="ec-order__stages-cell">
                      {stages.length === 0 ? (
                        <span className="ec-stage is-active">
                          {contract.currentStage || '작성'}
                        </span>
                      ) : (
                        <div className="ec-stages">
                          {stages.map((stage, index) => (
                            <span
                              key={`${contract.contractId}-${stage.stageId ?? stage.stageName}-${index}`}
                              className={`ec-stage is-${resolveStageStatus(stages, stage, contract)}`}
                            >
                              {stage.stageName}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default OrderProgressWidget;
